import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import FormikContainer from "../../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../../utils/genericTranslations.js";
import { selectCategoryByIdentifier } from "../../selectors";
import {
  fetchMalfunctionCategoryById,
  removeMalfunctionCategory,
  saveMalfunctionCategory,
} from "../../thunks.js";

import CategoryForm from "./CategoryForm.jsx";

const messages = {
  category: {
    id: "modules.Malfunctions.components.CategoryEditor.category",
    description: "Category label.",
    defaultMessage: "Category",
  },
  notFoundCategory: {
    id: "modules.Malfunctions.components.CategoryEditor.notFoundCategory",
    description: "Not found category message.",
    defaultMessage: "No category found!",
  },
  deleteMalfunctionCategory: {
    id: "modules.Malfunctions.components.CategoryEditor.deleteMalfunctionCategory",
    description: "Delete label.",
    defaultMessage: "Delete category",
  },
  deleteMalfunctionCategoryConfirmation: {
    id: "modules.Malfunctions.components.CategoryEditor.deleteMalfunctionCategoryConfirmation",
    description: "Delete confirm label.",
    defaultMessage: "Are you sure you want to delete this category?",
  },
};

const CategoryDetail = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const category = useSelector((state) =>
    selectCategoryByIdentifier(state, { identifier }),
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchMalfunctionCategoryById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    if (identifier && !category) {
      fetchData();
    }
  }, [category, identifier, dispatch]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const body = { values: { ...values } };

    try {
      await dispatch(
        saveMalfunctionCategory(body, category ? category.id : undefined),
      );
      navigate("/malfunction-form#categories", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const validate = (values) => {
    const errors = {};
    const required = ["label", "value", "vehicleType"];
    required.forEach((k) => {
      if (!values[k]) errors[k] = "Verplicht";
    });
    return errors;
  };

  const handleRemoveCategory = async () => {
    try {
      await dispatch(removeMalfunctionCategory(category.id));
      navigate("/malfunction-form#categories", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)} </strong>
    );
  }

  if (!category && identifier) {
    return <strong>{intl.formatMessage(messages.notFoundCategory)}</strong>;
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {intl.formatMessage(messages.category)}{" "}
        {category
          ? intl.formatMessage(genericTranslations.edit).toLowerCase()
          : intl.formatMessage(genericTranslations.add).toLowerCase()}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={category || {}}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={CategoryForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteMalfunctionCategory)}
          bodyText={intl.formatMessage(
            messages.deleteMalfunctionCategoryConfirmation,
          )}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveCategory}
        />
      </Card.Body>
    </Card>
  );
};

export default CategoryDetail;
